import {
  ButtonGroup,
  type ButtonGroupProps,
  type IconButtonProps,
  type MenuProps,
} from '@chakra-ui/react';
import { IconDotGrid1x3Vertical } from 'central-icons/IconDotGrid1x3Vertical';
import classNames from 'classnames';
import { type ReactNode, useState } from 'react';

import {
  Button,
  Menu as UiMenu,
  MenuButton as UiMenuButton,
  MenuItem as UiMenuItem,
  MenuList as UiMenuList,
} from '@revelio/ui';

import styles from './GridActionButton.module.css';

export const GridActionButton = ({
  label,
  onClick,
  additionalActions = [],
  groupProps,
  menuButtonProps,
  menuPlacement = 'bottom-end',
}: {
  label: string;
  onClick?: () => void;
  additionalActions?: {
    label: string;
    onClick: () => void;
    isDisabled?: boolean;
    icon?: ReactNode;
    testId?: string;
    onHighlightChange?: (highlighted: boolean) => void;
  }[];
  groupProps?: ButtonGroupProps;
  menuButtonProps?: IconButtonProps;
  menuPlacement?: MenuProps['placement'];
}) => {
  const hasAdditional = additionalActions.length > 0;
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  return (
    <ButtonGroup
      isAttached={hasAdditional}
      variant="outline"
      colorScheme="navyBlue"
      width="140px"
      height="2rem"
      borderRadius="full"
      opacity={isMenuOpen ? 1 : 0.88}
      transition="opacity 0.2s ease-in-out"
      _hover={{ opacity: 1 }}
      className={styles.group}
      {...groupProps}
    >
      <Button
        onClick={onClick}
        variant="default"
        color="primary"
        size="sm"
        className={classNames(
          styles.pillHalf,
          hasAdditional && styles.pillHalfAttached
        )}
      >
        {label}
      </Button>
      {hasAdditional && (
        <UiMenu
          onOpenChange={(open) => {
            setIsMenuOpen(open);
            if (open) return;
            for (const action of additionalActions) {
              action.onHighlightChange?.(false);
            }
          }}
        >
          <UiMenuButton>
            <Button
              color="primary"
              size="sm"
              aria-label={menuButtonProps?.['aria-label'] ?? 'More options'}
              className={styles.themedMenuTrigger}
            >
              <IconDotGrid1x3Vertical size={16} />
            </Button>
          </UiMenuButton>
          <UiMenuList align="end">
            {additionalActions.map((action) => (
              <UiMenuItem
                key={action.label}
                onClick={action.onClick}
                disabled={action.isDisabled}
                icon={action.icon}
                data-testid={action.testId}
                onFocus={() => action.onHighlightChange?.(true)}
                onBlur={() => action.onHighlightChange?.(false)}
              >
                {action.label}
              </UiMenuItem>
            ))}
          </UiMenuList>
        </UiMenu>
      )}
    </ButtonGroup>
  );
};
